export type ExceptionSimulateMode = 'off' | 'shadow' | 'on'

const VALID_EXCEPTION_SIMULATE_MODES: ExceptionSimulateMode[] = ['off', 'shadow', 'on']

function resolveExceptionSimulateMode(): ExceptionSimulateMode {
  const raw = process.env.VUE_APP_EXCEPTION_SIMULATE_MODE
  return VALID_EXCEPTION_SIMULATE_MODES.includes(raw as ExceptionSimulateMode)
    ? (raw as ExceptionSimulateMode)
    : 'off'
}

export function useFeatureFlags() {
  return {
    flags: {
      enabledPermissions: process.env.VUE_APP_ENABLED_PERMISSIONS === 'true',
      enabledPolicies: process.env.VUE_APP_ENABLED_POLICIES === 'true',
      enabledDSPoliciesPermissions: process.env.VUE_APP_ENABLED_DS_POLICIES_PERMISSIONS === 'true',
      enabledHandoverHistory: process.env.VUE_APP_ENABLED_HANDOVER_HISTORY === 'true',
      enabledManagementExceptionalities:
        process.env.VUE_APP_ENABLED_MANAGEMENT_EXCEPTIONALITIES === 'true',
      enabledObservationsSystem: process.env.VUE_APP_ENABLED_OBSERVATIONS_SYSTEM === 'true',
      enabledReports: process.env.VUE_APP_ENABLED_REPORTS === 'true',
      enabledStationHistory: process.env.VUE_APP_ENABLED_STATION_HISTORY === 'true',
      enabledManagement: process.env.VUE_APP_ENABLED_MANAGEMENT === 'true',
      enabledSmartAllocation: process.env.VUE_APP_ENABLED_SMART_ALLOCATION === 'true',
      enabledDoubleIdHeader: process.env.VUE_APP_ENABLED_DOUBLE_ID_HEADER === 'true',
      enabledDsAttendancePolicy: process.env.VUE_APP_ENABLED_DS_ATTENDANCE_POLICY === 'true',
      // PRD 32 — folha de monitoramento v2 (pré-preenchimento da data, classificação de
      // linha pela RN-03, bloqueio do avanço com motivo). Uma flag para o PRD inteiro.
      enabledDsAttendanceSheetV2: process.env.VUE_APP_ENABLED_DS_ATTENDANCE_SHEET_V2 === 'true',
      enabledMedicalEvaluation: process.env.VUE_APP_ENABLED_MEDICAL_EVALUATION === 'true',
      enabledApprovalFlow: process.env.VUE_APP_ENABLED_APPROVAL_FLOW === 'true',
      enabledApprovalEngine: process.env.VUE_APP_ENABLED_APPROVAL_ENGINE === 'true',
      // Exige Análise de Risco além de Operação na raiz do fluxo de excepcionalidade,
      // com o operador travado em "Todos - E". Ausente = false = só Operação obrigatória.
      enabledMandatoryRiskValidator:
        process.env.VUE_APP_ENABLED_MANDATORY_RISK_VALIDATOR === 'true',
      enabledHandoverV2: process.env.VUE_APP_ENABLED_HANDOVER_V2 === 'true',
      enabledExceptionalitySummary: process.env.VUE_APP_ENABLED_EXCEPTIONALITY_SUMMARY === 'true',
      enabledWebPushNotifications: process.env.VUE_APP_ENABLED_WEB_PUSH_NOTIFICATIONS === 'true',
      enabledFacialBiometry: process.env.VUE_APP_ENABLED_FACIAL_BIOMETRY === 'true',
      // PRD 00 — fila de avaliação de evidências dos registros de presença lançados no painel.
      enabledCheckValidation: process.env.VUE_APP_ENABLED_CHECK_VALIDATION === 'true',
      // Home de /aprovacoes em cards (uma rota por fila) no lugar das abas.
      enabledApprovalsHub: process.env.VUE_APP_ENABLED_APPROVALS_HUB === 'true',
      enabledClarity: process.env.VUE_APP_ENABLED_CLARITY === 'true',
      enabledPolicyModules: process.env.VUE_APP_ENABLED_POLICY_MODULES === 'true',
      enabledPostStation: process.env.VUE_APP_ENABLED_POST_STATION === 'true',
      enabledConsecutiveHoursLimit: process.env.VUE_APP_ENABLED_CONSECUTIVE_HOURS_LIMIT === 'true',
      enabledPermanenceRecord: process.env.VUE_APP_ENABLED_PERMANENCE_RECORD === 'true',
      // Fila "Pagamentos prioritários" (À vista + Antecipe). Depende do módulo
      // `priority-payments` do backend estar em PROD.
      enabledPriorityPayments: process.env.VUE_APP_ENABLED_PRIORITY_PAYMENTS === 'true',
      // Dashboard mensal: Setor/Especialidade, lentes, Mês e Calendário.
      enabledDashboardV2: process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true',
      // Onda 2.1: Matriz Setor × Especialidade. Só vale com a Dashboard V2 também ligada.
      enabledDashboardMatrix:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MATRIX === 'true',
      // Onda 2.3: Timeline por setor (visão de um dia). Só vale com a Dashboard V2 também ligada.
      enabledDashboardTimeline:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_TIMELINE === 'true',
      // Central de vagas (Onda 2.5a): vagas abertas e aguardando confirmação. Independe da V2.
      enabledDashboardVacancyCenter:
        process.env.VUE_APP_ENABLED_DASHBOARD_VACANCY_CENTER === 'true',
      // Tela Demanda (Onda 2.2): só vale junto com a Dashboard V2.
      enabledDashboardDemand:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_DEMAND === 'true',
      // Mapa da unidade + Modo TV (Onda 2.4): só vale junto com a Dashboard V2.
      enabledDashboardMap:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MAP === 'true',
      // Layout de celular do /dashboard (spec 2026-09-30). Só vale com a Dashboard V2 ligada.
      enabledDashboardMobile:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MOBILE === 'true',
      // Dashboard no celular v2 (spec 2026-10-04): grade com zoom e alternador. Só vale com a do
      // v1 ligada; desligar volta ao v1 (decisão 23), não à grade espremida.
      enabledDashboardMobileV2:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MOBILE === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MOBILE_V2 === 'true',
      // Celular v3 (SPEC-v3 §8, S1 com zoom livre): Semana/Mês viram "Escala" com a grade S1.
      // Precedência sobre a v2; desligar volta à v2 (se ligada) ou à v1. Não exige a do v2.
      enabledDashboardMobileV3:
        process.env.VUE_APP_ENABLED_DASHBOARD_V2 === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MOBILE === 'true' &&
        process.env.VUE_APP_ENABLED_DASHBOARD_MOBILE_V3 === 'true',
      // Paleta pastel única da escala (SPEC-v3 §7, decisões 57/59/60): cards, legendas e abas
      // do /dashboard (desktop e celular), Central de vagas e Demanda. Liga sozinha.
      enabledStationPaletteV3: process.env.VUE_APP_ENABLED_STATION_PALETTE_V3 === 'true',
      // Chip "Vagas abertas" + lentes padrão + fluxo "Lente ▾" (SPEC-v3 §9, decisões 42/48/56d/66).
      // Desktop e celular v2/v3; tira o "Só problemas" do v2. Liga sozinha.
      enabledDashboardQuickLenses: process.env.VUE_APP_ENABLED_DASHBOARD_QUICK_LENSES === 'true',
      // Aba Solicitações v2 (spec 2026-09-25, Onda 1) e o filtro "Modalidade de registro"
      // nas outras abas. Depende do PR5 do backend (`activity-requests/*`) em PROD.
      enabledActivityRequestsV2: process.env.VUE_APP_ENABLED_ACTIVITY_REQUESTS_V2 === 'true',
      // Portal do Afiliado Antecipe (spec 2026-09-30): layout `affiliate`, rotas /afiliado/*,
      // Gestão › Afiliados e "ver portal". Sem a flag as rotas não existem (NotFound).
      enabledAntecipeAffiliate: process.env.VUE_APP_ENABLED_ANTECIPE_AFFILIATE === 'true',
      // medical-docs-qualification (WEB1/WEB2): fila "Revisão de documentos", passo
      // "Qualificação exigida", alocação com bloqueio/pré-alocação e grupos de
      // qualificação da Central. Depende do backend com ENABLED_PROFESSIONAL_DOCUMENTS.
      enabledProfessionalDocuments: process.env.VUE_APP_ENABLED_PROFESSIONAL_DOCUMENTS === 'true',
      // Comunicados com recibo de leitura/ciência (PoC Novo Hamburgo, V-b): menu e rotas
      // /comunicados/*. Sem a flag as rotas não existem (NotFound). Backend: ENABLED_ANNOUNCEMENTS.
      enabledAnnouncements: process.env.VUE_APP_ENABLED_ANNOUNCEMENTS === 'true',
      // Termos de ciência (PRD26): menu e rotas /termos-de-ciencia/*. Sem a flag as rotas
      // não existem (NotFound). Backend: ENABLED_TERMS e as rotas /admin/terms.
      enabledTerms: process.env.VUE_APP_ENABLED_TERMS === 'true',
      enabledSplitScheduleVisibility:
        process.env.VUE_APP_ENABLED_SPLIT_SCHEDULE_VISIBILITY === 'true',
      // Frente B (spec antecipe-registration 2026-09-30, §6): matriz de fontes aceitas na
      // política de registro, selo de elegibilidade na auditoria e badges em Pagamentos
      // prioritários. Ligar só depois dos planos 02/03 do backend em PROD.
      enabledPaymentEligibility: process.env.VUE_APP_ENABLED_PAYMENT_ELIGIBILITY === 'true',
      // Frente A (Q8): campo "Regra para antecipação" na regra da política de pagamento.
      // Ligar só depois do plano 03 do backend em PROD: antes disso o `whitelist` do
      // backend descarta o campo em silêncio.
      enabledAntecipeProportionalityRule:
        process.env.VUE_APP_ENABLED_ANTECIPE_PROPORTIONALITY_RULE === 'true',
      // Bloqueio temporário do upload de evidência pelo painel (set/2026), LIGADO
      // em PROD desde 10/09.
      //
      // F2/Q32 manda substituí-lo pelo bloqueio de verdade, servido pelo backend
      // em `registration-settings` (`manualCheckBlockEnabled`). Mas os dois
      // deploys não são o mesmo: enquanto a rota do backend não existir, ela
      // responde 404 e o campo cai no default `false` — remover a env var aqui
      // reabriria em PROD exatamente o upload que o kill switch fechou.
      //
      // Por isso a flag CONTINUA: é o fallback enquanto o campo do backend não
      // chega. Com `manualCheckBlockEnabled` presente, quem manda é ele.
      // Removê-la é tarefa de follow-up, depois da flag do backend ligada em PROD.
      // Ausente = false = comportamento atual (upload liberado).
      disabledManualEvidenceUpload: process.env.VUE_APP_DISABLED_MANUAL_EVIDENCE_UPLOAD === 'true',
      // Botão "Registrar" na tela de entrada (out/2026): desligado por decisão de produto,
      // mantido atrás de flag para poder voltar. Ausente = false = botão escondido.
      enabledSignup: process.env.VUE_APP_ENABLED_SIGNUP === 'true',
      // acceptance-terms (WEB-1..3): modal de convite em 3 passos, faixa na grade, painel do
      // convite e página /aceite/:token. Exige o backend com ALLOCATION_INVITE_ENABLED=true
      // (desligado lá, as rotas respondem 404). Ausente = false = modal de hoje (D55, D91).
      enabledAllocationInvite: process.env.VUE_APP_ENABLED_ALLOCATION_INVITE === 'true',
      exceptionSimulateMode: resolveExceptionSimulateMode()
    }
  }
}
